import { useApi } from "@/lib/api/useApi";
import { useMutate } from "@/lib/api/useMutate";
import { queryCache } from "@/lib/api/queryCache";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { addToCart } from "@/lib/api/cart";

import {
  cartQuery,
  categoryTabsQuery,
  favoriteIdsQuery,
  favoritesQuery,
  productsQuery,
  subcategoriesQuery,
} from "@/lib/api/queries";
import { toggleFavorite } from "@/lib/api/wishlist";
import type { Product } from "@/lib/api/types";
import { Icon } from "@/components/store/Icon";
import { ProductGridSkeleton, ProductCardSkeleton } from "@/components/store/ProductCardSkeleton";
import { Reveal } from "@/components/motion/Reveal";
import { StoreLayout } from "@/components/layout/StoreLayout";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode } from "swiper/modules";
import "swiper/css";
import "swiper/css/free-mode";

import { useFormatters } from "@/lib/format";
import { useLocalizedContent } from "@/lib/localize";
import { imgUrl } from "@/lib/format";
import { useApp } from "@/contexts/AppContext";
import { ProductCard } from "@/components/store/ProductCard";


export const searchSchema = {
  category: "",
  sub: "all",
  sort: "popularity",
  page: 1,
};

function ShopPage() {
  const { t } = useTranslation();
  const { rtl } = useApp();

  const { formatPrice, formatNumber } = useFormatters();
  const { productName, productCategory } = useLocalizedContent();
  const [searchParams] = useSearchParams();
  const urlCategory = searchParams.get("category") || "all";
  const sub = searchParams.get("sub") || "all";
  const sort = searchParams.get("sort") || "popularity";
  const page = parseInt(searchParams.get("page") || "1", 10);
  const navigate = useNavigate();

  const tq = categoryTabsQuery();
  const { data: tabs = [], loading: loadingTabs, isFetching: isFetchingTabs } = useApi(tq.queryKey, tq.queryFn);

  // Category slugs come from the backend; a slug left over in the URL (deleted
  // or renamed category) falls back to the first real tab instead of rendering
  // an empty shop.
  const category =
    urlCategory === "all" || !urlCategory
      ? "all"
      : tabs.length === 0
        ? urlCategory
        : tabs.some((tabItem) => tabItem.slug === urlCategory)
          ? urlCategory
          : "all";

  // Category query is always ready from frame 0
  const categoryReady = true;

  const pq = productsQuery({ category, sub, sort, page });
  const sq = subcategoriesQuery(category);
  const fq = favoriteIdsQuery();

  const { data: result, isFetching: isFetchingProducts, loading: loadingProducts } = useApi(
    pq.queryKey,
    pq.queryFn,
    { enabled: categoryReady }
  );
  const { data: subs = [] } = useApi(sq.queryKey, sq.queryFn, { enabled: category !== "all" });
  const { data: favoriteIds = [] } = useApi(fq.queryKey, fq.queryFn);

  const favoriteMutation = useMutate(
    (productId: string) => toggleFavorite(productId),
    {
      onSuccess: (ids, productId) => {
        queryCache.invalidate(favoriteIdsQuery().queryKey);
        queryCache.invalidate(favoritesQuery().queryKey);
        toast.success(ids.includes(productId) ? t("favorites.added") : t("favorites.removed"));
      },
      onError: (error: Error) => {
        if (error.message.includes("bearer token") || error.message.includes("jwt")) {
          toast.error(t("auth.pleaseLogin", { defaultValue: "Please login first" }));
        } else {
          toast.error(error.message);
        }
      },
    }
  );

  const addMutation = useMutate(
    (product: Product) =>
      addToCart({
        productId: product.id,
        color: product.colors?.[0]?.name ?? "",
        size: product.sizes?.[0] ?? "",
      }),

    {
      onSuccess: (_data, product) => {
        queryCache.invalidate(cartQuery().queryKey);
        toast.success(t("common.addedToBag", { name: productName(product.name) }));
      },
      onError: (error: Error) => toast.error(error.message),
    }
  );

  const activeTab = tabs.find((tabItem) => tabItem.slug === category);
  const label = t(`categories.${category}`, {
    defaultValue: activeTab ? productCategory(activeTab.label) : t("shop.title"),
  });

  const subItems = [{ slug: "all", label: t("shop.allItems") }, ...subs.map((s) => ({
    slug: s.slug,
    label: productCategory(s.label),
  }))];

  const showSkeleton = !result || (loadingProducts && (!result.items || result.items.length === 0));

  return (
    <StoreLayout>
      <main className="pt-10 pb-section-gap-desktop max-w-7xl mx-auto px-grid-margin">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-6 cursor-pointer hover:bg-surface-container-low transition-colors">
          <ArrowLeft className="w-4 h-4 me-2" />{t('product.back', { defaultValue: 'Back' })}
        </Button>
        <Reveal as="header" className="mb-10">
          <div className="flex items-center gap-2 text-label-sm font-label-sm text-on-surface-variant/60 mb-4">
            <Link to="/" className="hover:text-primary transition-colors">
              {t("shop.breadcrumbHome").toUpperCase()}
            </Link>
            <Icon name="chevron_right" className="text-[12px] rtl:rotate-180" />
            <Link to="/shop" className="text-on-surface hover:text-primary transition-colors">
              {t("shop.breadcrumbShop").toUpperCase()}
            </Link>
          </div>

          {/* Main categories */}
          {tabs.length === 0 && (loadingTabs || isFetchingTabs) ? (
            <div className="flex items-center gap-6 sm:gap-8 border-b border-outline-variant/30 pb-3 overflow-hidden animate-pulse">
              <div className="h-8 w-20 bg-surface-container-high rounded-md" />
              <div className="h-8 w-24 bg-surface-container rounded-md" />
              <div className="h-8 w-28 bg-surface-container rounded-md" />
              <div className="h-8 w-20 bg-surface-container rounded-md" />
              <div className="h-8 w-28 bg-surface-container rounded-md hidden sm:block" />
            </div>
          ) : (
            <Swiper
              modules={[FreeMode]}
              freeMode
              slidesPerView="auto"
              spaceBetween={32}
              dir={rtl ? "rtl" : "ltr"}
              key={`main-${rtl ? "rtl" : "ltr"}`}
              className="border-b border-outline-variant/30"
            >
              <SwiperSlide className="!w-auto">
                <Link
                  to={`/shop?category=all&sub=all&sort=${sort}&page=1`}
                  className={`block pb-3 tracking-wider transition-colors text-headline-md font-headline-md ${
                    category === "all"
                      ? "text-primary border-b-2 border-secondary"
                      : "text-on-surface-variant/40 hover:text-primary"
                  }`}
                >
                  {t("shop.allItems")}
                </Link>
              </SwiperSlide>
              {tabs.map((tab) => (
                <SwiperSlide key={tab.slug} className="!w-auto">
                  <Link
                    to={`/shop?category=${tab.slug}&sub=all&sort=${sort}&page=1`}
                    className={`block pb-3 tracking-wider transition-colors text-headline-md font-headline-md ${
                      tab.slug === category
                        ? "text-primary border-b-2 border-secondary"
                        : "text-on-surface-variant/40 hover:text-primary"
                    }`}
                  >
                    {t(`categories.${tab.slug}`, { defaultValue: tab.label })}
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          )}

          {/* Subcategory strip (only shown when a specific category is selected) */}
          {category !== "all" && category !== "" && subs.length > 0 && (
            <Swiper
              modules={[FreeMode]}
              freeMode
              slidesPerView="auto"
              spaceBetween={8}
              dir={rtl ? "rtl" : "ltr"}
              key={`sub-${rtl ? "rtl" : "ltr"}-${category}`}
              className="mt-3 pb-1"
            >
              {subItems.map((item) => (
                <SwiperSlide key={item.slug} className="!w-auto">
                  <Link
                    to={`/shop?category=${category}&sub=${item.slug}&sort=${sort}&page=1`}
                    className={`block rounded-full border px-4 py-1.5 text-[12px] font-label-sm uppercase tracking-widest transition-all ${
                      item.slug === sub
                        ? "border-secondary bg-secondary-container/60 text-primary"
                        : "border-outline-variant/50 text-on-surface-variant hover:border-primary hover:text-primary"
                    }`}
                  >
                    {item.label}
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          )}

        </Reveal>

        <div className="flex gap-gutter">


          {/* Grid */}
          <section className="flex-grow min-w-0">
            <motion.div
              layout
              animate={{ opacity: isFetchingProducts ? 0.6 : 1 }}
              transition={{ duration: 0.2 }}
              className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-gutter gap-y-12 items-stretch"
            >
              <AnimatePresence mode="popLayout">
                {showSkeleton ? (
                  Array.from({ length: 8 }).map((_, index) => (
                    <motion.div
                      key={`skeleton-${index}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <ProductCardSkeleton />
                    </motion.div>
                  ))
                ) : (
                  result?.items.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))
                )}
              </AnimatePresence>
            </motion.div>

            {!showSkeleton && result?.items.length === 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-20 flex flex-col items-center justify-center"
              >
                <Icon name="info" className="text-4xl text-on-surface-variant/40 mb-3" />
                <p className="text-on-surface-variant text-sm font-medium">
                  {t("shop.noProducts", { defaultValue: "No products found in this category" })}
                </p>
              </motion.div>
            )}

            {/* Pagination */}
            {(result?.totalPages ?? 1) > 1 && (
              <div className="mt-20 flex justify-center items-center gap-3">
                <button
                  type="button"
                  aria-label={t("common.previousPage")}
                  disabled={page <= 1}
                  onClick={() =>
                    navigate(`/shop?category=${category}&sub=${sub}&sort=${sort}&page=${page - 1}`)
                  }
                  className="w-11 h-11 flex items-center justify-center rounded-full border border-outline-variant/60 text-on-surface-variant transition-all hover:border-secondary hover:text-secondary disabled:opacity-30"
                >
                  <Icon name="west" className="rtl:rotate-180" />
                </button>
                <div className="flex gap-2">
                  {Array.from({ length: result?.totalPages ?? 1 }, (_, index) => index + 1).map(
                    (num) => (
                      <button
                        key={num}
                        type="button"
                        onClick={() =>
                          navigate(`/shop?category=${category}&sub=${sub}&sort=${sort}&page=${num}`)
                        }
                        className={`w-11 h-11 flex items-center justify-center rounded-full text-[13px] font-label-sm transition-all ${
                          num === result?.page
                            ? "bg-primary text-on-primary luxury-shadow"
                            : "text-on-surface-variant hover:bg-surface-container"
                        }`}
                      >
                        {formatNumber(num)}
                      </button>
                    ),
                  )}
                </div>
                <button
                  type="button"
                  aria-label={t("common.nextPage")}
                  disabled={page >= (result?.totalPages ?? 1)}
                  onClick={() =>
                    navigate(`/shop?category=${category}&sub=${sub}&sort=${sort}&page=${page + 1}`)
                  }
                  className="w-11 h-11 flex items-center justify-center rounded-full border border-outline-variant/60 text-on-surface-variant transition-all hover:border-secondary hover:text-secondary disabled:opacity-30"
                >
                  <Icon name="east" className="rtl:rotate-180" />
                </button>
              </div>
            )}
          </section>
        </div>
      </main>
    </StoreLayout>
  );
}

export default ShopPage;
